import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import { Plus, Search, Users, Check, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  addEmployee,
  assignEmployeeProjects,
  deleteEmployee,
  getAllEmployees,
  getProjects,
  setEmployeeStatus,
  updateEmployee,
} from "../../redux/actionCreator";
import {
  add_employee_loading,
  all_employees,
  employee_list_loading,
  employees_page_details,
  project_data,
} from "../../redux/commonReducer";
import "./employeesList.scss";

const ROLES = [
  { value: "employee", label: "Employee" },
  { value: "admin", label: "Admin" },
];

const blankForm = {
  id: null,
  firstname: "",
  lastname: "",
  email: "",
  employee_id: "",
  phone: "",
  blood_group: "",
  role: "employee",
  status: "approved",
  projects: [],
};

export default function EmployeesList() {
  const dispatch = useDispatch();

  const employees = useSelector(all_employees) || [];
  const listLoading = useSelector(employee_list_loading);
  const pageDetails = useSelector(employees_page_details);
  const projects = useSelector(project_data) || [];
  const saving = useSelector(add_employee_loading);

  const [showForm, setShowForm] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [data, setData] = useState(blankForm);
  const [err, setErr] = useState({});
  const [page, setPage] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [projTarget, setProjTarget] = useState(null);
  const [projTargetIds, setProjTargetIds] = useState([]);

  const projectOptions = useMemo(
    () =>
      projects.map((p) => ({
        value: p._id,
        label: p.project_name || p.name || p._id,
      })),
    [projects]
  );

  useEffect(() => {
    dispatch(getProjects({ page: 0, limit: 100 }));
  }, []);

  useEffect(() => {
    dispatch(getAllEmployees({ page, limit: 10, keyword }));
  }, [page, keyword]);

  const refresh = () =>
    dispatch(getAllEmployees({ page, limit: 10, keyword }));

  const openCreate = () => {
    setData(blankForm);
    setErr({});
    setShowForm(true);
  };

  const openEdit = (emp) => {
    setData({
      id: emp._id,
      firstname: emp.firstname || "",
      lastname: emp.lastname || "",
      email: emp.email || "",
      employee_id: emp.employee_id || "",
      phone: emp.phone || "",
      blood_group: emp.blood_group || "",
      role: emp.role || "employee",
      status: emp.status || "approved",
      projects: emp.projects || [],
    });
    setErr({});
    setShowForm(true);
  };

  const onChange = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
    setErr((e) => ({ ...e, [key]: "" }));
  };

  const validate = () => {
    const t = {};
    if (!data.firstname?.trim()) t.firstname = "Required";
    if (!data.email?.trim()) t.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim()))
      t.email = "Invalid email";
    setErr(t);
    return Object.keys(t).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    const payload = {
      firstname: data.firstname.trim(),
      lastname: data.lastname.trim(),
      email: data.email.trim().toLowerCase(),
      employee_id: data.employee_id.trim(),
      phone: data.phone.trim(),
      blood_group: data.blood_group.trim(),
      role: data.role,
    };
    if (data.id) {
      dispatch(
        updateEmployee({ id: data.id, ...payload }, (ok) => {
          if (ok) {
            setShowForm(false);
            refresh();
          }
        })
      );
    } else {
      dispatch(
        addEmployee(
          { ...payload, status: data.status, projects: data.projects },
          (ok) => {
            if (ok) {
              setShowForm(false);
              refresh();
            }
          }
        )
      );
    }
  };

  // Three-state cycle:
  //   pending  → approved  (admin reviewing a self-registered user)
  //   approved → blocked   (revoke access)
  //   blocked  → approved  (restore)
  const toggleStatus = (emp) => {
    const cur = emp.status || "approved";
    const next = cur === "blocked" || cur === "pending" ? "approved" : "blocked";
    const prompt =
      cur === "pending"
        ? `Approve ${emp.email}? They'll be able to sign in immediately.`
        : next === "blocked"
          ? `Block ${emp.email}? They will no longer be able to log in.`
          : `Re-approve ${emp.email}?`;
    if (!window.confirm(prompt)) return;
    dispatch(setEmployeeStatus(emp._id, next, (ok) => ok && refresh()));
  };

  // Explicit Reject for pending users — sets status=blocked so re-applying
  // with the same email gives a clean "exists" path (no orphan rows).
  const rejectPending = (emp) => {
    if (!window.confirm(`Reject ${emp.email}'s access request?`)) return;
    dispatch(setEmployeeStatus(emp._id, "blocked", (ok) => ok && refresh()));
  };

  const removeEmployee = (emp) => {
    if (!window.confirm(`Delete ${emp.email}? This cannot be undone.`)) return;
    dispatch(deleteEmployee(emp._id, (ok) => ok && refresh()));
  };

  const openProjects = (emp) => {
    setProjTarget(emp);
    setProjTargetIds(emp.projects || []);
    setShowProjects(true);
  };

  const saveProjects = () => {
    dispatch(
      assignEmployeeProjects(projTarget._id, projTargetIds, (ok) => {
        if (ok) {
          setShowProjects(false);
          refresh();
        }
      })
    );
  };

  return (
    <div className="employees-page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Employees</h2>
          <small className="page-sub">
            Only employees added here can sign in with Google.
          </small>
        </div>
        <div className="page-actions">
          <div className="search-box">
            <span className="search-icon"><Search size={16} /></span>
            <input
              className="form-control"
              placeholder="Search by name"
              value={keyword}
              onChange={(e) => {
                setPage(0);
                setKeyword(e.target.value);
              }}
            />
          </div>
          <Button className="btn-primary-solid" onClick={openCreate}>
            <Plus size={16} className="me-1" /> Add Employee
          </Button>
        </div>
      </div>

      <div className="dash-panel emp-panel">
        <div className="panel-head">
          <h3>
            All employees
            <span className="count-pill">
              {pageDetails?.totalCount ?? employees.length}
            </span>
          </h3>
        </div>

        <div className="emp-table-wrap">
          <table className="emp-table">
            <thead>
              <tr>
                <th style={{ width: 48 }}>#</th>
                <th>Employee</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th className="text-center">Projects</th>
                <th className="text-end" style={{ width: 1 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr>
                  <td colSpan={7} className="empty-row">
                    <Spinner animation="border" size="sm" /> Loading…
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-row">
                    <div className="empty-state">
                      <div className="empty-art"><Users size={40} strokeWidth={1.2} /></div>
                      <h4>No employees yet</h4>
                      <p>
                        Add your first employee — they'll be able to sign in
                        with their Gmail.
                      </p>
                      <Button
                        className="btn-primary-solid"
                        onClick={openCreate}
                      >
                        ＋ Add Employee
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                employees.map((emp, idx) => {
                  const fullName =
                    [emp.firstname, emp.lastname].filter(Boolean).join(" ") ||
                    "—";
                  const initials = (emp.firstname || emp.email || "?")
                    .slice(0, 1)
                    .toUpperCase();
                  return (
                    <tr key={emp._id}>
                      <td className="muted">{page * 10 + idx + 1}</td>
                      <td>
                        <div className="emp-cell">
                          <div className="emp-avatar">{initials}</div>
                          <div>
                            <div className="emp-name">{fullName}</div>
                            <div className="emp-id">
                              {emp.employee_id || "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{emp.email}</td>
                      <td>
                        <span className={`role-chip role-${emp.role}`}>
                          {emp.role}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`status-badge ${emp.status || "approved"}`}
                        >
                          {emp.status || "—"}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="proj-pill">
                          {(emp.projects || []).length}
                        </span>
                      </td>
                      <td className="text-end">
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            size="sm"
                            variant="light"
                            className="row-actions-btn"
                          >
                            ⋯
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => openEdit(emp)}>
                              Edit
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => openProjects(emp)}>
                              Assign projects
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            {emp.status === "pending" ? (
                              <>
                                <Dropdown.Item
                                  onClick={() => toggleStatus(emp)}
                                  className="text-success"
                                >
                                  <Check size={12} className="me-1" /> Approve request
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => rejectPending(emp)}
                                  className="text-danger"
                                >
                                  <X size={12} className="me-1" /> Reject request
                                </Dropdown.Item>
                              </>
                            ) : (
                              <Dropdown.Item
                                onClick={() => toggleStatus(emp)}
                                className={
                                  emp.status === "blocked"
                                    ? "text-success"
                                    : "text-warning"
                                }
                              >
                                {emp.status === "blocked"
                                  ? "Approve access"
                                  : "Block access"}
                              </Dropdown.Item>
                            )}
                            <Dropdown.Item
                              onClick={() => removeEmployee(emp)}
                              className="text-danger"
                            >
                              Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pageDetails?.totalPages > 1 && (
          <div className="panel-footer">
            <span className="muted">{pageDetails?.page_status}</span>
            <div className="pager">
              <Button
                size="sm"
                variant="light"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                variant="light"
                className="ms-2"
                disabled={page + 1 >= (pageDetails?.totalPages || 1)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </div>

      <Modal
        className="main__modal"
        show={showForm}
        onHide={() => setShowForm(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <h2 className="m-0">{data.id ? "Edit Employee" : "Add Employee"}</h2>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>First name *</Form.Label>
                <Form.Control
                  value={data.firstname}
                  onChange={(e) => onChange("firstname", e.target.value)}
                />
                <span className="text-danger">{err.firstname}</span>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Last name</Form.Label>
                <Form.Control
                  value={data.lastname}
                  onChange={(e) => onChange("lastname", e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Email (Gmail used for login) *</Form.Label>
                <Form.Control
                  type="email"
                  value={data.email}
                  disabled={!!data.id}
                  onChange={(e) => onChange("email", e.target.value)}
                />
                <span className="text-danger">{err.email}</span>
                {data.id && (
                  <small className="text-muted">
                    Email cannot be changed after creation.
                  </small>
                )}
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Employee ID</Form.Label>
                <Form.Control
                  value={data.employee_id}
                  onChange={(e) => onChange("employee_id", e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  value={data.phone}
                  onChange={(e) => onChange("phone", e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Blood group</Form.Label>
                <Form.Control
                  value={data.blood_group}
                  onChange={(e) => onChange("blood_group", e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Role</Form.Label>
                <Select
                  options={ROLES}
                  value={ROLES.find((r) => r.value === data.role)}
                  onChange={(o) => onChange("role", o.value)}
                />
              </Form.Group>
            </div>
            {!data.id && (
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Initial projects</Form.Label>
                  <Select
                    isMulti
                    options={projectOptions}
                    value={projectOptions.filter((o) =>
                      data.projects.includes(o.value)
                    )}
                    onChange={(opts) =>
                      onChange(
                        "projects",
                        (opts || []).map((o) => o.value)
                      )
                    }
                  />
                  <small className="text-muted">
                    You can change this later via Projects.
                  </small>
                </Form.Group>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : data.id ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showProjects}
        onHide={() => setShowProjects(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <h4 className="m-0">
            Projects for {projTarget?.email || ""}
          </h4>
        </Modal.Header>
        <Modal.Body>
          <Select
            isMulti
            options={projectOptions}
            value={projectOptions.filter((o) =>
              projTargetIds.includes(o.value)
            )}
            onChange={(opts) =>
              setProjTargetIds((opts || []).map((o) => o.value))
            }
          />
          <small className="text-muted">
            Only assigned projects appear in the employee's dashboard.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProjects(false)}>
            Cancel
          </Button>
          <Button onClick={saveProjects}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
